/**
 * Sales Execution Swarm Portal
 * 1,000-agent autonomous sales and business development system
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, TrendingUp, DollarSign, Globe, Shield, Users,
  Zap, Package, ShoppingCart, Truck, CheckCircle, AlertTriangle,
  RefreshCw, BarChart3, Eye, MousePointerClick, Target,
  ArrowUpRight, ArrowDownRight, Clock, Loader2
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIVISION_COLORS = {
  intelligence: "#3b82f6",
  deal_sourcing: "#10b981",
  content_creation: "#8b5cf6",
  distribution: "#f59e0b",
  sales_execution: "#ef4444",
  procurement: "#06b6d4",
  fulfillment: "#ec4899",
  customer_success: "#14b8a6",
};

const SEVERITY_STYLES = {
  info: "text-blue-400 bg-blue-500/10",
  success: "text-green-400 bg-green-500/10",
  warning: "text-amber-400 bg-amber-500/10",
};

const divisionIcons = {
  intelligence: Globe,
  deal_sourcing: TrendingUp,
  content_creation: Zap,
  distribution: Activity,
  sales_execution: ShoppingCart,
  procurement: Package,
  fulfillment: Truck,
  customer_success: CheckCircle,
};

function MetricCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const colorMap = {
    blue: "bg-blue-500/10 text-blue-400",
    green: "bg-green-500/10 text-green-400",
    amber: "bg-amber-500/10 text-amber-400",
    purple: "bg-purple-500/10 text-purple-400",
  };
  return (
    <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorMap[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-[var(--qn-foreground-muted)] truncate">{label}</p>
          <p className="text-xl font-bold text-[var(--qn-foreground)]">{value}</p>
          {sub && <p className={`text-xs ${colorMap[color]?.split(" ")[1] || "text-[var(--qn-foreground-muted)]"}`}>{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function ActivityItem({ activity }) {
  const Icon = divisionIcons[activity.division] || Activity;
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-[var(--qn-surface)] transition-colors" data-testid={`activity-${activity.activity_id}`}>
      <div className={`p-1.5 rounded-md mt-0.5 ${SEVERITY_STYLES[activity.severity] || SEVERITY_STYLES.info}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[var(--qn-foreground)] leading-snug">{activity.message}</p>
        <p className="text-xs text-[var(--qn-foreground-muted)] mt-0.5 font-mono">{activity.agent_id}</p>
      </div>
    </div>
  );
}

export default function SalesSwarmPortal() {
  const [overview, setOverview] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState("intelligence");
  const [agents, setAgents] = useState([]);
  const [deals, setDeals] = useState([]);
  const [content, setContent] = useState([]);
  const [activity, setActivity] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [activeTab, setActiveTab] = useState("agents");

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-swarm/overview`);
      setOverview(res.data);
    } catch (err) {
      console.error("Failed to fetch swarm overview:", err);
    }
  }, []);

  const fetchAgents = useCallback(async (division) => {
    try {
      const res = await axios.get(`${API}/sales-swarm/agents?division=${division}`);
      setAgents(res.data.agents.slice(0, 30));
    } catch (err) {
      console.error("Failed to fetch agents:", err);
    }
  }, []);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-swarm/deals?limit=50`);
      setDeals(res.data.deals);
    } catch (err) {
      console.error("Failed to fetch deals:", err);
    }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-swarm/content?limit=50`);
      setContent(res.data.content);
    } catch (err) {
      console.error("Failed to fetch content:", err);
    }
  }, []);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-swarm/activity?limit=30`);
      setActivity(res.data.activities);
    } catch (err) {
      console.error("Failed to fetch activity:", err);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-swarm/analytics`);
      setAnalytics(res.data);
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchOverview();
      await fetchActivity();
      setLoading(false);
    };
    init();
  }, [fetchOverview, fetchActivity]);

  useEffect(() => {
    if (selectedDivision) fetchAgents(selectedDivision);
  }, [selectedDivision, fetchAgents]);

  useEffect(() => {
    if (activeTab === "deals") fetchDeals();
    if (activeTab === "content") fetchContent();
    if (activeTab === "analytics") fetchAnalytics();
  }, [activeTab, fetchDeals, fetchContent, fetchAnalytics]);

  const seedSwarm = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/sales-swarm/seed`);
      await fetchOverview();
      await fetchAgents(selectedDivision);
      await fetchActivity();
      if (activeTab === "deals") await fetchDeals();
      if (activeTab === "content") await fetchContent();
      if (activeTab === "analytics") await fetchAnalytics();
    } catch (err) {
      console.error("Seed error:", err);
    }
    setSeeding(false);
  };

  if (loading || !overview) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="sales-swarm-loading">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-cyan-400" />
          <p className="text-sm text-[var(--qn-foreground-muted)]">Loading Brand Management Swarm...</p>
        </div>
      </div>
    );
  }

  const totalTasksToday = agents.reduce((s, a) => s + (a.tasks_completed_today || 0), 0);

  return (
    <div className="min-h-screen p-4 md:p-6 space-y-5 pb-20" data-testid="sales-swarm-portal">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--qn-foreground)]">
            Brand Management Swarm
          </h1>
          <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">
            1,000 autonomous agents &middot; 8 divisions &middot; 195 countries &middot; brand-safety protected
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { fetchOverview(); fetchActivity(); }} variant="ghost" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={seedSwarm} disabled={seeding} size="sm" data-testid="seed-swarm-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white">
            {seeding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />}
            {seeding ? "Seeding..." : "Seed & Activate Swarm"}
          </Button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard icon={Users} label="Active Agents" value={overview.active_agents} sub={`of ${overview.total_agents} total`} color="blue" />
        <MetricCard icon={TrendingUp} label="Deals Today" value={overview.deals_today} sub="Active opportunities" color="green" />
        <MetricCard icon={DollarSign} label="Revenue Today" value={overview.revenue_today} sub="Net margins" color="amber" />
        <MetricCard icon={Shield} label="Brand Safety Clear" value={overview.kultur_clearance_rate} sub="Cultural safety" color="purple" />
      </div>

      {/* Division Grid + Activity Feed */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Division Selector */}
        <Card className="xl:col-span-2 p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="division-selector">
          <h2 className="text-base font-semibold text-[var(--qn-foreground)] mb-3">Agent Divisions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {Object.entries(overview.divisions).map(([key, division]) => {
              const Icon = divisionIcons[key];
              const isSelected = selectedDivision === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedDivision(key)}
                  data-testid={`division-${key}`}
                  className={`p-3 rounded-lg border transition-all text-left ${
                    isSelected
                      ? "border-cyan-500 bg-cyan-500/10"
                      : "border-[var(--qn-border)] bg-[var(--qn-surface)] hover:border-cyan-500/40"
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    {Icon && <Icon className="w-4 h-4" style={{ color: DIVISION_COLORS[key] }} />}
                    <Badge variant="outline" className="text-xs px-1.5 py-0">{division.agent_count}</Badge>
                  </div>
                  <p className="font-medium text-[var(--qn-foreground)] text-xs leading-tight">{division.name}</p>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Activity Feed */}
        <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)] max-h-[320px] overflow-hidden flex flex-col" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-[var(--qn-foreground)]">Live Activity</h2>
            <Badge variant="secondary" className="text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block mr-1 animate-pulse" />
              Live
            </Badge>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1 pr-1 scrollbar-thin">
            {activity.length === 0 ? (
              <p className="text-sm text-[var(--qn-foreground-muted)] text-center py-8">
                No activity yet. Seed the swarm to see live data.
              </p>
            ) : (
              activity.slice(0, 15).map((a, i) => <ActivityItem key={a.activity_id || i} activity={a} />)
            )}
          </div>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="bg-[var(--qn-surface)]" data-testid="swarm-tabs">
          <TabsTrigger value="agents" data-testid="swarm-tab-agents">Agents</TabsTrigger>
          <TabsTrigger value="deals" data-testid="swarm-tab-deals">Deals</TabsTrigger>
          <TabsTrigger value="content" data-testid="swarm-tab-content">Content</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="swarm-tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* AGENTS TAB */}
        <TabsContent value="agents">
          <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="agents-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-[var(--qn-foreground)]">
                {overview.divisions[selectedDivision]?.name} — Agents
              </h3>
              <Badge variant="outline">{agents.length} shown</Badge>
            </div>
            {agents.length === 0 ? (
              <div className="text-center py-10">
                <AlertTriangle className="w-10 h-10 text-amber-400/30 mx-auto mb-2" />
                <p className="text-sm text-[var(--qn-foreground-muted)]">No agents found. Click "Seed & Activate Swarm" to populate.</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {agents.map((agent) => (
                  <div
                    key={agent.agent_id}
                    data-testid={`agent-row-${agent.agent_id}`}
                    className="p-2.5 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-[var(--qn-foreground)] truncate">{agent.agent_id}</p>
                      <p className="text-xs text-[var(--qn-foreground-muted)]">{agent.role?.replace(/_/g, " ")}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge
                        variant={agent.status === "active" ? "default" : agent.status === "working" ? "secondary" : "outline"}
                        className="text-xs"
                      >
                        {agent.status}
                      </Badge>
                      <span className="text-xs text-[var(--qn-foreground-muted)] whitespace-nowrap">
                        {agent.tasks_completed_today || 0} tasks
                      </span>
                      <span className="text-xs text-green-400 font-medium whitespace-nowrap">
                        {agent.success_rate || 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* DEALS TAB */}
        <TabsContent value="deals">
          <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="deals-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-[var(--qn-foreground)]">Deal Opportunities</h3>
              <Badge variant="outline">{deals.length} deals</Badge>
            </div>
            {deals.length === 0 ? (
              <div className="text-center py-10">
                <TrendingUp className="w-10 h-10 text-green-400/30 mx-auto mb-2" />
                <p className="text-sm text-[var(--qn-foreground-muted)]">No deals yet. Seed the swarm to generate opportunities.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {deals.map((deal) => (
                  <div
                    key={deal.deal_id}
                    data-testid={`deal-${deal.deal_id}`}
                    className="p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]"
                  >
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-[var(--qn-foreground)] truncate">{deal.product_name}</p>
                        <p className="text-xs text-[var(--qn-foreground-muted)]">{deal.source_platform} &middot; {deal.type?.replace(/_/g, " ")}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge
                          variant="outline"
                          className={deal.net_margin_pct >= 30 ? "text-green-400 border-green-500/30" : "text-amber-400 border-amber-500/30"}
                        >
                          {deal.net_margin_pct}% margin
                        </Badge>
                        <Badge variant="secondary" className="text-xs capitalize">{deal.status}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[var(--qn-foreground-muted)]">
                      <span>Buy: ${deal.acquisition_cost?.toFixed(2)}</span>
                      <span>Sell: ${deal.estimated_sell_price?.toFixed(2)}</span>
                      <span>Markets: {deal.target_markets?.join(", ")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* CONTENT TAB */}
        <TabsContent value="content">
          <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="content-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-[var(--qn-foreground)]">Content Pipeline</h3>
              <Badge variant="outline">{content.length} items</Badge>
            </div>
            {content.length === 0 ? (
              <div className="text-center py-10">
                <Zap className="w-10 h-10 text-purple-400/30 mx-auto mb-2" />
                <p className="text-sm text-[var(--qn-foreground-muted)]">No content yet. Seed the swarm to create culturally-validated content.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {content.map((item) => (
                  <div
                    key={item.content_id}
                    data-testid={`content-${item.content_id}`}
                    className="p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]"
                  >
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="text-xs capitalize">{item.type}</Badge>
                          <Badge variant="outline" className="text-xs capitalize">{item.status}</Badge>
                        </div>
                        <p className="text-sm text-[var(--qn-foreground)] line-clamp-2">{item.content_body}</p>
                      </div>
                      {item.kultur_validation && (
                        <Badge variant="outline" className="text-purple-400 border-purple-500/30 flex-shrink-0 ml-2">
                          <Shield className="w-3 h-3 mr-1" /> {(item.kultur_validation.risk_score * 100).toFixed(0)}% safe
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[var(--qn-foreground-muted)] mt-1">
                      <span>Markets: {item.target_markets?.join(", ")}</span>
                      <span>Platforms: {item.target_platforms?.join(", ")}</span>
                      {item.engagement && (
                        <>
                          <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {item.engagement.views?.toLocaleString()}</span>
                          <span className="flex items-center gap-0.5"><MousePointerClick className="w-3 h-3" /> {item.engagement.clicks?.toLocaleString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics">
          {!analytics ? (
            <Card className="p-8 border-[var(--qn-border)] bg-[var(--qn-card)] text-center">
              <BarChart3 className="w-10 h-10 text-[var(--qn-foreground-muted)] mx-auto mb-2 opacity-30" />
              <p className="text-sm text-[var(--qn-foreground-muted)]">Seed the swarm to see analytics.</p>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="analytics-panel">
              {/* Engagement Summary */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-4 h-4 text-blue-400" />
                    <span className="text-xs text-[var(--qn-foreground-muted)]">Total Views</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--qn-foreground)]">
                    {(analytics.engagement?.total_views || 0).toLocaleString()}
                  </p>
                </Card>
                <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]">
                  <div className="flex items-center gap-2 mb-1">
                    <MousePointerClick className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-[var(--qn-foreground-muted)]">Total Clicks</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--qn-foreground)]">
                    {(analytics.engagement?.total_clicks || 0).toLocaleString()}
                  </p>
                </Card>
                <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-amber-400" />
                    <span className="text-xs text-[var(--qn-foreground-muted)]">Conversions</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--qn-foreground)]">
                    {(analytics.engagement?.total_conversions || 0).toLocaleString()}
                  </p>
                </Card>
              </div>

              {/* Division Performance Chart */}
              <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="division-performance-chart">
                <h3 className="text-base font-semibold text-[var(--qn-foreground)] mb-3">Division Performance — Tasks Today</h3>
                {analytics.division_performance?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={analytics.division_performance.map(d => ({
                      ...d,
                      shortName: d.name?.split(" ")[0] || d.division,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="shortName" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                      <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "rgba(15,23,42,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                        labelStyle={{ color: "#fff" }}
                      />
                      <Bar dataKey="tasks_today" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Tasks Today" />
                      <Bar dataKey="active" fill="#10b981" radius={[4, 4, 0, 0]} name="Active Agents" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-[var(--qn-foreground-muted)] text-center py-8">No data</p>
                )}
              </Card>

              {/* Deals by Type + Market Coverage */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="deals-by-type-chart">
                  <h3 className="text-base font-semibold text-[var(--qn-foreground)] mb-3">Deals by Type</h3>
                  {analytics.deals_by_type?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={analytics.deals_by_type.map(d => ({
                            name: d.type?.replace(/_/g, " "),
                            value: d.count,
                            margin: d.avg_margin_pct,
                          }))}
                          cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                          paddingAngle={3} dataKey="value"
                          label={({ name, value }) => `${name} (${value})`}
                        >
                          {analytics.deals_by_type.map((_, i) => (
                            <Cell key={i} fill={Object.values(DIVISION_COLORS)[i % 8]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-[var(--qn-foreground-muted)] text-center py-8">No deals</p>
                  )}
                </Card>

                <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="market-coverage-chart">
                  <h3 className="text-base font-semibold text-[var(--qn-foreground)] mb-3">Market Coverage</h3>
                  {analytics.market_coverage?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analytics.market_coverage.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis type="number" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                        <YAxis dataKey="market" type="category" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} width={40} />
                        <Tooltip
                          contentStyle={{ backgroundColor: "rgba(15,23,42,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                        />
                        <Bar dataKey="deals" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Deals" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-[var(--qn-foreground-muted)] text-center py-8">No market data</p>
                  )}
                </Card>
              </div>

              {/* Content Status */}
              {analytics.content_by_status && Object.keys(analytics.content_by_status).length > 0 && (
                <Card className="p-4 border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="content-status-chart">
                  <h3 className="text-base font-semibold text-[var(--qn-foreground)] mb-3">Content Pipeline Status</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(analytics.content_by_status).map(([status, count]) => (
                      <div key={status} className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)] text-center">
                        <p className="text-2xl font-bold text-[var(--qn-foreground)]">{count}</p>
                        <p className="text-xs text-[var(--qn-foreground-muted)] capitalize">{status}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* kultur.dev Protection Badge */}
      <div className="fixed bottom-4 left-4 z-40" data-testid="kultur-badge">
        <div className="px-3 py-2 rounded-lg border border-purple-500/30 bg-purple-900/40 backdrop-blur-xl flex items-center gap-2">
          <Shield className="w-4 h-4 text-purple-400" />
          <div>
            <p className="text-xs font-semibold text-purple-300">Brand Safety Protected</p>
            <p className="text-[10px] text-purple-400/70">195 countries &middot; Cultural safety active</p>
          </div>
        </div>
      </div>
    </div>
  );
}
